/** Waiting this long for the new version to take over, then the page is reloaded anyway. */
export const TAKEOVER_TIMEOUT_MS = 2000;

export interface Worker {
  postMessage(message: unknown): void;
}

export interface ReloadDeps {
  /** The new version's service worker if it still waits; null if another tab activated it. */
  waiting(): Promise<Worker | null>;
  onTakeover(listener: () => void): void;
  reload(): void;
  setTimeout(fn: () => void, ms: number): unknown;
}

/**
 * Loads the new version whatever state the service worker is in. Asking a waiting worker to
 * take over only reloads once it did; when another tab of the app already activated it there
 * is nothing waiting any more, and the page just has to be loaded again.
 */
export async function reloadToUpdate(deps: ReloadDeps): Promise<void> {
  let done = false;
  const reload = () => {
    if (done) return;
    done = true;
    deps.reload();
  };
  let waiting: Worker | null = null;
  try {
    waiting = await deps.waiting();
  } catch {
    // No service worker access (private mode, unsupported): a reload is all there is.
  }
  if (!waiting) {
    reload();
    return;
  }
  deps.onTakeover(reload);
  deps.setTimeout(reload, TAKEOVER_TIMEOUT_MS);
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** The browser's service worker, for `reloadToUpdate`. */
export const browserReloadDeps: ReloadDeps = {
  async waiting() {
    if (!('serviceWorker' in navigator)) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    return registration?.waiting ?? null;
  },
  onTakeover(listener) {
    navigator.serviceWorker.addEventListener('controllerchange', listener, { once: true });
  },
  reload: () => window.location.reload(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
};
